/**
 * FE-4533 — design-system token substitution.
 *
 * Two namespaces, both written as `{{...}}` inside any style value:
 *
 *   {{_s.<name>}}  a variable of the ANSWERING THEME  (envelope `styles`)
 *   {{_c.<name>}}  a project config token             (envelope `config`)
 *
 * MIRROR OF THE EDITOR, NOT SHARED CODE. `fractal-fe` resolves the same two
 * namespaces while painting its canvas (`features/themes/lib/designTokens.ts`);
 * the repos ship separately, so this is a deliberate duplicate in the manner of
 * `styleClasses` and ROOT_FLEX_DEFAULTS. If the unresolved-token rule below
 * changes, BOTH move together or the editor shows something production will not.
 *
 * UNRESOLVED TOKENS STAY AS AUTHORED. `{{_s.nope}}` renders as that very text
 * rather than as an empty string: a typo that silently removes a style leaves
 * the author nothing to see, while visible token text is a bad outcome they can
 * at least act on. FE-4533 states this outright.
 *
 * WHY THE CLIENT DOES THIS AT ALL: the backend delivers `styles` with its `_c`
 * references INTACT — verified against a live payload, where the answering
 * theme arrived as `{accent: "{{_c.brand_color}}"}`. It selects the theme, but
 * it does not resolve the tokens inside it.
 */

/** `{{ _s.name }}` / `{{_c.name}}` — tolerant of inner whitespace. */
const TOKEN_PATTERN = /\{\{\s*_(s|c)\.([^}\s]+?)\s*\}\}/g;

export interface TokenScope {
  /** The answering theme's variables — the `_s` namespace. */
  styles?: Record<string, unknown> | null;
  /** The branch's config token VALUES — the `_c` namespace. */
  config?: Record<string, unknown> | null;
}

const lookup = (scope: TokenScope, ns: string, name: string): unknown =>
  (ns === 's' ? scope.styles?.[name] : scope.config?.[name]);

/**
 * Substitute every `_s`/`_c` token in ONE string.
 *
 * A token whose value is absent, `null` or `undefined` is left standing. Every
 * other value is stringified, so a numeric token (`card_radius: 16`) is usable
 * in a style value without the author quoting it.
 */
export const substituteTokensInString = (value: string, scope: TokenScope): string =>
  value.replace(TOKEN_PATTERN, (whole, ns: string, name: string) => {
    const resolved = lookup(scope, ns, name);
    return resolved === undefined || resolved === null ? whole : String(resolved);
  });

/**
 * Deep-substitute every string in a style tree.
 *
 * Walks arrays and nested objects alike, because a token is legal anywhere a
 * value is: `background[0].value`, `states.hover.color`, `border.radius`. The
 * input is returned UNTOUCHED when nothing changed, so a screen using no tokens
 * keeps object identity and pays no rebuild.
 */
export const substituteTokensDeep = <T>(value: T, scope: TokenScope): T => {
  if (typeof value === 'string') {
    const next = substituteTokensInString(value, scope);
    return (next === value ? value : next) as unknown as T;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((entry) => {
      const next = substituteTokensDeep(entry, scope);
      if (next !== entry) changed = true;
      return next;
    });
    return (changed ? out : value) as unknown as T;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
      const next = substituteTokensDeep(entry, scope);
      if (next !== entry) changed = true;
      out[key] = next;
    });
    return (changed ? out : value) as unknown as T;
  }
  return value;
};

/**
 * Resolve `{{_c.*}}` INSIDE the answering theme's variables, before anything
 * reads them.
 *
 * THE LAYERING, and it is one-directional:
 *
 *     config  ──▶  themes  ──▶  screens / classes / templates
 *
 * A theme variable may reference a config token (`accent: "{{_c.brand_color}}"`),
 * so a screen writing `{{_s.accent}}` gets the config value through it. The
 * reverse is not supported: config is the base layer and never reads a theme, so
 * the graph cannot cycle and no depth limit is needed.
 *
 * Doing this ONCE here is what keeps every style leaf a single pass. Substituting
 * `_s` first would otherwise yield a value that itself still contains
 * `{{_c.*}}`, and every consumer would have to know to run a second pass.
 */
export const resolveThemeVariables = (
  styles: Record<string, unknown> | null | undefined,
  config: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!styles || typeof styles !== 'object') return {};
  return substituteTokensDeep(styles, { config });
};
